/**
 * The mission's daylight: one directional sun with a shadow map, and a hemisphere fill (05 §7).
 * Split out of `StaticWorld`, which was at its line limit, because it is one self-contained job -
 * the shadow frustum has to follow the view, snapped along the LIGHT's own axes so shadow edges do
 * not shimmer while the camera scrolls (05 §8.2).
 */

import { Color, DirectionalLight, HemisphereLight, type Light, type Scene, Vector3 } from 'three';
import type { PixelCamera } from '../camera/PixelCamera';
import type { Grade } from '../palette';

/**
 * Half-width of the sun's shadow box, metres. It has to cover the GROUND THE CAMERA CAN SEE, and the
 * camera does not keep one zoom: the insertion run pulls back to `FLYOVER_PPM_SCALE` (0.34) and shows
 * about 117 m across where the ground view shows 40. At a fixed 34 the box covered the middle of that
 * and nothing else, so every tree and hut outside it had no shadow and popped one on as the aircraft
 * flew south - "shadows warping in". Reported in play.
 */
const GROUND_EXTENT = 34;
/** Enough for the widest view the camera ever takes; past this the map's texels are too coarse to help. */
const MAX_EXTENT = 96;
/**
 * The box grows in steps. The zoom eases continuously, and an extent that followed it continuously
 * would change the texel size every frame - which is the very shimmer the snapping below exists to
 * stop. Stepping means it is constant for all but a handful of frames per zoom.
 */
const EXTENT_STEP = 8;
/** Room for something standing at the very edge of the view to cast INTO it. */
const EXTENT_MARGIN = 6;
const SHADOW_MAP = 2048;
/** How much a full stroke adds to the sun and the sky: enough to read as a flash, short of a wash-out. */
const FLASH_GAIN = 2.2;

export class WorldLights {
  private readonly sun: DirectionalLight;
  private readonly sunDir: Vector3;
  private readonly lightRight: Vector3;
  private readonly lightUp: Vector3;
  private readonly target = new Vector3();
  private readonly lights: Light[] = [];
  private extent = GROUND_EXTENT;
  /** What the grade says the sun and the sky are worth, before a stroke of lightning adds to them. */
  private readonly baseSun: number;
  private readonly baseHemi: number;
  private readonly hemi: HemisphereLight;

  constructor(private readonly scene: Scene, grade: Grade) {
    this.sunDir = new Vector3(...grade.sunDir).normalize();
    this.sun = new DirectionalLight(new Color(grade.sunColor), grade.sunIntensity);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(SHADOW_MAP, SHADOW_MAP);
    const sc = this.sun.shadow.camera;
    sc.left = -GROUND_EXTENT;
    sc.right = GROUND_EXTENT;
    sc.top = GROUND_EXTENT;
    sc.bottom = -GROUND_EXTENT;
    sc.near = 10;
    sc.far = 240;
    this.sun.shadow.bias = -0.0006;
    this.sun.shadow.normalBias = 0.04;
    const hemi = new HemisphereLight(new Color(grade.skyColor), new Color(grade.groundColor), grade.hemiIntensity);
    scene.add(this.sun, this.sun.target, hemi);
    this.lights.push(this.sun, hemi);
    this.hemi = hemi;
    this.baseSun = grade.sunIntensity;
    this.baseHemi = grade.hemiIntensity;
    // Light-space basis, matching how three.js orients a directional light's shadow camera.
    this.lightRight = new Vector3(0, 1, 0).cross(this.sunDir).normalize();
    this.lightUp = this.sunDir.clone().cross(this.lightRight).normalize();
  }

  /** Keep the shadow frustum on the view, snapped along the light's own axes (05 §8.2). */
  follow(camera: PixelCamera): void {
    this.fitTo(camera);
    const texel = (this.extent * 2) / SHADOW_MAP;
    const px = camera.focus.dot(this.lightRight);
    const py = camera.focus.dot(this.lightUp);
    const ox = Math.round(px / texel) * texel - px;
    const oy = Math.round(py / texel) * texel - py;
    this.target.copy(camera.focus).addScaledVector(this.lightRight, ox).addScaledVector(this.lightUp, oy);
    this.sun.target.position.copy(this.target);
    this.sun.position.copy(this.target).addScaledVector(this.sunDir, 120);
    this.sun.target.updateMatrixWorld();
  }

  /**
   * Widen the box to hold everything the camera can see, in steps. The visible ground is a rectangle
   * of `viewMetres`; its half-diagonal is the furthest corner from the focus the box has to reach.
   */
  private fitTo(camera: PixelCamera): void {
    const view = camera.viewMetres;
    const need = Math.hypot(view.width, view.depth) / 2 + EXTENT_MARGIN;
    const stepped = Math.ceil(need / EXTENT_STEP) * EXTENT_STEP;
    const wanted = Math.min(MAX_EXTENT, Math.max(GROUND_EXTENT, stepped));
    if (wanted === this.extent) return;
    this.extent = wanted;
    const sc = this.sun.shadow.camera;
    sc.left = -wanted;
    sc.right = wanted;
    sc.top = wanted;
    sc.bottom = -wanted;
    sc.updateProjectionMatrix();
  }

  /** What the box covers now, metres either side of the focus: for the tests, and for the debug layer. */
  get shadowExtent(): number {
    return this.extent;
  }

  /**
   * A stroke of lightning, 0 to 1 (02 §5). It is light, so it arrives as light: the sun and the sky
   * both lift, which throws real shadows for the instant it lasts rather than washing the frame
   * white. At night that is the whole effect - a dark map is briefly a lit one, and the garrison
   * can see the length of it (`sim/weather.ts` lifts their sighting range by the same figure).
   */
  setFlash(flash: number): void {
    const k = 1 + FLASH_GAIN * Math.min(1, Math.max(0, flash));
    this.sun.intensity = this.baseSun * k;
    this.hemi.intensity = this.baseHemi * k;
  }

  dispose(): void {
    this.scene.remove(this.sun.target);
    for (const light of this.lights) {
      this.scene.remove(light);
      light.dispose();
    }
    this.lights.length = 0;
  }
}
